import React, { useState, useEffect } from 'react';
import Autosuggest from 'react-autosuggest';

import { ENTITY_TYPES_TO_ICONS } from '../common/selectorsConsts';
import {
    HTTP_OPTIONS,
    SEARCH_DEBOUNCE_DELAY,
    SEARCH_MIN_CHARS,
    ACCOUNT_TYPE_TO_DISPLAY
} from '@silverfort-nodelibs/common/utils/consts';

import connectivityErrorIcon from '../../../common/Images/connectivity_error.png';
import disabledUserIcon from '../../../common/Images/disabled_user.png';

import styles from './css/SearchTable.module.css';
import { httpGet } from '../../../utils';
import { unresolvedResources } from '../../../Policy/common';

const SearchTable = ({
    suggestions,
    suggestionsQuery,
    placeholderText,
    onSuggestionsQueryChanged,
    onSelectSuggestion,
    showExcluded,
    disableAdd = true,
    searchEntitiesTypes,
    setSuggestions,
    focusedResource,
    currentSelectedEntities,
    showFullPath,
    selectAllEntities,
    disabled,
    alwaysSearch,
    accountTypeToDisplay,
    authTypes,
    searchTableClassName = '',
    showUnresolvedResources = false
}) => {
    const [lastSentData, setLastSentData] = useState({ query: '', entitiesTypes: [] });
    const [lastRequestID, setLastRequestID] = useState(-1);
    const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
    const [isConnectivityError, setIsConnectivityError] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        const timer = setTimeout(async () => {
            let escapedQuery = searchQuery.trim();
            if ((escapedQuery === '' || escapedQuery.length < SEARCH_MIN_CHARS) && !alwaysSearch) {
                setLastSentData({ ...lastSentData, query: '' });
                return [];
            }
            escapedQuery = encodeURIComponent(escapedQuery);
            if (
                lastSentData.query === escapedQuery &&
                searchEntitiesTypes.length === lastSentData.entitiesTypes.length &&
                searchEntitiesTypes.every((type) => lastSentData.entitiesTypes.includes(type))
            ) {
                return;
            }

            const newRequestID = lastRequestID + 1;
            const reqUrl = `/users/searchEntitiesByTypes/${escapedQuery}/${newRequestID}`;
            setLastRequestID(newRequestID);
            setIsLoadingSuggestions(true);
            try {
                const response = await httpGet(reqUrl, {
                    [HTTP_OPTIONS.REQ_OPTIONS]: {
                        params: {
                            hideDeleted: true,
                            entitiesTypes: searchEntitiesTypes,
                            authTypes: authTypes
                        }
                    }
                });

                if (response.data.requestID !== newRequestID.toString()) {
                    return;
                }
                setIsLoadingSuggestions(false);
                setIsConnectivityError(false);
                const newSuggestions = [...response.data.Results];
                if (showUnresolvedResources && !currentSelectedEntities.some((entity) => entity.guid === unresolvedResources.guid)) {
                    newSuggestions.unshift(unresolvedResources);
                }
                setSuggestions(newSuggestions);
                setLastSentData({ query: escapedQuery, entitiesTypes: searchEntitiesTypes });
            } catch (error) {
                setIsConnectivityError(true);
                console.error(error);
            }
        }, SEARCH_DEBOUNCE_DELAY);

        return () => clearTimeout(timer);
    }, [searchQuery, lastSentData, lastRequestID, searchEntitiesTypes, setSuggestions, alwaysSearch]);

    const renderSuggestion = (suggestion) => {
        const focusedEntity = focusedResource ? focusedResource : {};
        let suggestionName;
        if (accountTypeToDisplay === ACCOUNT_TYPE_TO_DISPLAY.FULL_PATH.value) {
            suggestionName = suggestion.path;
        } else if (accountTypeToDisplay === ACCOUNT_TYPE_TO_DISPLAY.SAM_ACCOUNT.value) {
            suggestionName = suggestion.sam;
        } else {
            suggestionName = suggestion.display_name || suggestion.name || '(deleted)';
        }

        return (
            <span
                className={`${styles.suggestionContainer} ${
                    suggestion.guid === focusedEntity.guid ? styles.focusSuggestion : ''
                } ${disabled && styles.disabled}`}
                onClick={(event) => !disabled && onSuggestionClicked(suggestion, event)}>
                <div className={styles.suggestionData} title={suggestion.path}>
                    <img
                        className={styles.suggestionIcon}
                        src={suggestion.is_disabled ? disabledUserIcon : ENTITY_TYPES_TO_ICONS[suggestion.type]}
                        alt=""
                    />
                    <div className={styles.suggestionNameContainer}>
                        <div
                            className={`${styles.suggestionName} ${suggestion.is_disabled && styles.disabledUserName}`}>
                            {suggestionName}
                            {suggestion.is_disabled && <span className={styles.disabledUserTag}>(Disabled)</span>}
                        </div>
                        <div className={styles.suggestionDomain}>
                            {suggestion.domain_name ? ` (${suggestion.domain_name})` : ''}
                        </div>
                    </div>
                </div>

                {currentSelectedEntities.findIndex((entity) => entity.guid === suggestion.guid) < 0 && (
                    <div className={styles.suggestionButtons}>
                        {showExcluded && suggestion.guid !== unresolvedResources.guid && (
                            <div
                                onClick={(event) => {
                                    !disabled && onSelectSuggestion(suggestion, true, event);
                                }}
                                className={styles.suggestionExcludeButton}>
                                <i className="fa fa-minus-circle"></i> Exclude
                            </div>
                        )}
                        {!selectAllEntities && !disableAdd && (
                            <div
                                onClick={(event) => !disabled && onSelectSuggestion(suggestion, false, event)}
                                className={styles.suggestionAddButton}>
                                <i className="fa fa-plus-circle"></i> Add
                            </div>
                        )}
                    </div>
                )}
            </span>
        );
    };

    const onSuggestionClicked = (suggestion, event) => {
        event.stopPropagation();
        const suggestionIndex = currentSelectedEntities.findIndex((entity) => entity.guid === suggestion.guid);
        if (suggestionIndex < 0 && !selectAllEntities) {
            onSelectSuggestion(suggestion, disableAdd, event);
        }
    };

    const specialSizeContainer = focusedResource ? true : false;

    return (
        <div
            className={`${styles.searchTable} ${
                specialSizeContainer ? styles.specialSizeSearchTable : ''
            } ${searchTableClassName}`}>
            {!isLoadingSuggestions && <i className={`${styles.searchIcon} fa fa-search`}></i>}
            {isLoadingSuggestions && !isConnectivityError && (
                <i className={`${styles.searchIcon} fa fa-refresh fa-spin fa-3x fa-fw`}></i>
            )}
            {isConnectivityError && (
                <div className={styles.connectivityErrorIcon} title={'Connectivity failure'}>
                    <img src={connectivityErrorIcon} alt="" />
                </div>
            )}

            <Autosuggest
                suggestions={suggestions}
                onSuggestionsFetchRequested={(query) => setSearchQuery(query.value)}
                alwaysRenderSuggestions={true}
                getSuggestionValue={() => suggestionsQuery}
                renderSuggestion={renderSuggestion}
                inputProps={{
                    placeholder: placeholderText,
                    value: suggestionsQuery,
                    onChange: (event) => onSuggestionsQueryChanged(event.target.value),
                    autoFocus: true
                }}
            />
        </div>
    );
};

export default SearchTable;
